class FaqFilter {
  constructor() {
    this.root = document.getElementById('faq-facets');
    this.dataElement = document.querySelector('[id^="faq-data-"]');
    this.searchInput = document.getElementById('faq-search-input');
    this.urlParams = {
      search: 'faq_keyword',
      pickup: 'faq_pickup',
      category: 'faq_category',
      product: 'faq_product',
    };
    this.state = {
      pickup: '',
      category: '',
      product: '',
      search: '',
    };

    this.pickupButtons = [];
    this.categoryButtons = [];
    this.productButtons = [];
    this.faqData = [];
    this.searchIndex = new Map();
    this.recordIndex = new Map();
    this.filterItems = [];

    this.categorySections = Array.from(document.querySelectorAll('.js-faq-category'));
  }

  static normalize(value) {
    return (value || '').trim();
  }

  static splitList(value) {
    return FaqFilter.normalize(value)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  static uniqueByKey(items) {
    const seen = new Set();
    return items.filter((item) => {
      if (!item || !item.key) return false;
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  }

  static buildButton(container, items, dataAttribute, type) {
    if (!container) return [];
    container.innerHTML = '';
    return items.map((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `faq-facets__button faq-facets__button--${type}`;
      button.dataset[type] = item.key;
      button.textContent = item.label;
      button.setAttribute(dataAttribute, item.key);
      container.appendChild(button);
      return button;
    });
  }

  static collectFacetItems(records, fieldName) {
    const collected = [];
    records.forEach((record) => {
      const values = Array.isArray(record[fieldName]) ? record[fieldName] : [];
      values.forEach((item) => {
        if (item && item.key && item.label) collected.push(item);
      });
    });
    return FaqFilter.uniqueByKey(collected);
  }

  static normalizeSearch(value) {
    return FaqFilter.normalize(value).toLowerCase();
  }

  static getUrlValue(name) {
    return FaqFilter.normalize(new URL(window.location.href).searchParams.get(name));
  }

  static setSearchInputValue(input, value) {
    if (!input) return;
    input.value = value;
  }

  bindButtons(buttons, key, applyFilter) {
    buttons.forEach((button) => {
      button.type = 'button';
      button.addEventListener('click', () => applyFilter(FaqFilter.normalize(button.dataset[key])));
    });
  }

  setButtonActiveState(buttons, key, value) {
    buttons.forEach((button) => {
      const active = FaqFilter.normalize(button.dataset[key]) === value && value !== '';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  isKnownFacetValue(buttons, key, value) {
    if (!value) return false;
    return buttons.some((button) => FaqFilter.normalize(button.dataset[key]) === value);
  }

  getStateFromUrl() {
    const pickup = FaqFilter.getUrlValue(this.urlParams.pickup);
    const category = FaqFilter.getUrlValue(this.urlParams.category);
    const product = FaqFilter.getUrlValue(this.urlParams.product);
    const search = FaqFilter.getUrlValue(this.urlParams.search);

    return {
      pickup: this.isKnownFacetValue(this.pickupButtons, 'pickup', pickup) ? pickup : '',
      category: this.isKnownFacetValue(this.categoryButtons, 'category', category) ? category : '',
      product: this.isKnownFacetValue(this.productButtons, 'product', product) ? product : '',
      search,
    };
  }

  syncStateToUrl() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const entries = [
      [this.urlParams.pickup, this.state.pickup],
      [this.urlParams.category, this.state.category],
      [this.urlParams.product, this.state.product],
      [this.urlParams.search, this.state.search],
    ];

    entries.forEach(([name, value]) => {
      if (value) {
        params.set(name, value);
      } else {
        params.delete(name);
      }
    });

    window.history.replaceState({}, '', `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`);
  }

  syncUiFromState() {
    this.setButtonActiveState(this.pickupButtons, 'pickup', this.state.pickup);
    this.setButtonActiveState(this.categoryButtons, 'category', this.state.category);
    this.setButtonActiveState(this.productButtons, 'product', this.state.product);
    FaqFilter.setSearchInputValue(this.searchInput, this.state.search);
  }

  matchesTags(item, pickup, category, product) {
    const record = this.recordIndex.get(item.dataset.handle);
    const pickupTags = (record && Array.isArray(record.pickup_tags)) ? record.pickup_tags.map((tag) => tag.key) : [];
    const categoryTags = (record && Array.isArray(record.category_tags)) ? record.category_tags.map((tag) => tag.key) : [];
    const productTags = (record && Array.isArray(record.product_tags)) ? record.product_tags.map((tag) => tag.key) : [];

    if (pickup && !pickupTags.includes(pickup)) return false;
    if (category && !categoryTags.includes(category)) return false;
    if (product && !productTags.includes(product)) return false;

    return true;
  }

  matchesSearch(item) {
    if (!this.state.search) return true;
    const handle = item.dataset.handle || '';
    let haystack = this.searchIndex.get(handle) || '';
    if (!haystack) {
      // Fallback: still allow keyword filtering from rendered DOM text.
      haystack = FaqFilter.normalizeSearch(item.textContent || '');
    }
    return haystack.includes(this.state.search);
  }

  updateFaqVisibility() {
    const hasPickup = this.state.pickup !== '';
    const hasCategory = this.state.category !== '';
    const hasProduct = this.state.product !== '';
    const hasSearch = this.state.search !== '';

    this.categorySections.forEach((section) => {
      let sectionHasVisibleItem = false;
      const excludedItems = Array.from(section.querySelectorAll('.js-faq-item--excluded'));

      const items = Array.from(section.querySelectorAll('.js-faq-item:not(.js-faq-item--excluded)'));

      items.forEach((item) => {
        const tagOk = this.matchesTags(item, this.state.pickup, this.state.category, this.state.product);
        const searchOk = this.matchesSearch(item);
        const visible = (!hasPickup || tagOk) && (!hasCategory || tagOk) && (!hasProduct || tagOk) && (!hasSearch || searchOk);
        item.hidden = !visible;
        if (visible) sectionHasVisibleItem = true;
      });

      const sectionHasOnlyExcludedItems = excludedItems.length > 0 && items.length === 0;
      section.hidden = !sectionHasVisibleItem && !sectionHasOnlyExcludedItems && (hasPickup || hasCategory || hasProduct || hasSearch);
      const heading = section.querySelector('h2');
      if (heading) heading.hidden = section.hidden;
    });
  }

  applyPickup(value) {
    this.state.pickup = this.state.pickup === value ? '' : value;
    this.syncUiFromState();
    this.syncStateToUrl();
    this.updateFaqVisibility();
  }

  applyCategory(value) {
    this.state.category = this.state.category === value ? '' : value;
    this.syncUiFromState();
    this.syncStateToUrl();
    this.updateFaqVisibility();
  }

  applyProduct(value) {
    this.state.product = this.state.product === value ? '' : value;
    this.syncUiFromState();
    this.syncStateToUrl();
    this.updateFaqVisibility();
  }

  applySearch(value) {
    this.state.search = FaqFilter.normalizeSearch(value);
    this.syncStateToUrl();
    this.updateFaqVisibility();
  }

  init() {
    if (!this.root || this.categorySections.length === 0) return;

    if (this.dataElement) {
      try {
        this.faqData = JSON.parse(this.dataElement.textContent || '[]');
      } catch (error) {
        this.faqData = [];
      }
    }

    this.faqData.forEach((record) => {
      this.searchIndex.set(record.handle, FaqFilter.normalizeSearch(record.search_text));
      this.recordIndex.set(record.handle, record);
    });

    this.filterItems = Array.from(document.querySelectorAll('.js-faq-item:not(.js-faq-item--excluded)'));

    const pickupContainer = document.getElementById('faq-pickup-facets');
    const categoryContainer = document.getElementById('faq-category-facets');
    const productContainer = document.getElementById('faq-product-facets');

    const pickupItems = FaqFilter.collectFacetItems(this.faqData, 'pickup_tags');
    const categoryItems = FaqFilter.collectFacetItems(this.faqData, 'category_tags');
    const productItems = FaqFilter.collectFacetItems(this.faqData, 'product_tags');

    this.pickupButtons = FaqFilter.buildButton(pickupContainer, pickupItems, 'data-pickup', 'pickup');
    this.categoryButtons = FaqFilter.buildButton(categoryContainer, categoryItems, 'data-category', 'category');
    if (productItems.length > 0) {
      this.productButtons = FaqFilter.buildButton(productContainer, productItems, 'data-product', 'product');
    } else {
      this.productButtons = productContainer
        ? Array.from(productContainer.querySelectorAll('[data-product]'))
        : [];
    }

    this.bindButtons(this.pickupButtons, 'pickup', this.applyPickup.bind(this));
    this.bindButtons(this.categoryButtons, 'category', this.applyCategory.bind(this));
    this.bindButtons(this.productButtons, 'product', this.applyProduct.bind(this));

    if (this.searchInput) {
      this.searchInput.addEventListener('input', (event) => {
        this.applySearch(event.target.value);
      });
    }

    this.state = {
      ...this.state,
      ...this.getStateFromUrl(),
    };
    this.syncUiFromState();

    this.updateFaqVisibility();
  }
}

new FaqFilter().init();
